// src/app/redux/reducers/offboardingReducer.js
import {
    SET_OFFBOARDING_LOADING,
    CLEAR_OFFBOARDING_ERROR,

    FETCH_OFFBOARDINGS_REQUEST,
    FETCH_OFFBOARDINGS_SUCCESS,
    FETCH_OFFBOARDINGS_FAIL,

    GET_OFFBOARDING_SUCCESS,
    GET_OFFBOARDING_FAIL,

    CREATE_OFFBOARDING_SUCCESS,
    UPDATE_OFFBOARDING_REQUEST,
    UPDATE_OFFBOARDING_SUCCESS,
    DELETE_OFFBOARDING_SUCCESS,

    FETCH_ETAPES_SUCCESS,
    FETCH_ETAPES_FAIL,

    CREATE_ETAPE_SUCCESS,
    UPDATE_ETAPE_REQUEST,
    UPDATE_ETAPE_SUCCESS,
    DELETE_ETAPE_SUCCESS,
} from "../actions/offboardingActions";

const initialState = {
    loading: false,
    error: null,

    offboardings: [],
    selectedOffboarding: null,

    // { [offboardingId]: OffboardingEtapeResponseDTO[] }
    etapesByOffboarding: {},
};

// helper: trouver offboardingId depuis une étape (selon format backend)
const getOffIdFromEtape = (e) =>
    e?.offboardingId ?? e?.offboarding_id ?? e?.offboarding?.id ?? null;

// helper: certains sagas renvoient { id, payload } au lieu de l'objet complet
const normalizeEntityPayload = (p) => {
    if (!p) return p;
    if (p && typeof p === "object" && "payload" in p && "id" in p) {
        const inner = p.payload || {};
        return { id: p.id, ...inner };
    }
    return p;
};

// helper: retrouver offboardingId d’une étape en scannant etapesByOffboarding
const findOffIdByEtapeId = (etapesByOffboarding, etapeId) => {
    const entries = Object.entries(etapesByOffboarding || {});
    for (const [offId, list] of entries) {
        if ((list || []).some((e) => String(e?.id) === String(etapeId))) {
            return String(offId);
        }
    }
    return null;
};

export default function offboardingReducer(state = initialState, action) {
    switch (action.type) {
        case SET_OFFBOARDING_LOADING:
            return { ...state, loading: action.payload };

        case CLEAR_OFFBOARDING_ERROR:
            return { ...state, error: null };

        // ===== OFFBOARDINGS LIST
        case FETCH_OFFBOARDINGS_REQUEST:
            return { ...state, loading: true, error: null };

        case FETCH_OFFBOARDINGS_SUCCESS:
            return { ...state, loading: false, error: null, offboardings: action.payload || [] };

        case FETCH_OFFBOARDINGS_FAIL:
            return { ...state, loading: false, error: action.payload };

        // ===== GET ONE
        case GET_OFFBOARDING_SUCCESS:
            return { ...state, loading: false, error: null, selectedOffboarding: action.payload };

        case GET_OFFBOARDING_FAIL:
            return { ...state, loading: false, error: action.payload };

        // ===== CREATE
        case CREATE_OFFBOARDING_SUCCESS:
            return { ...state, offboardings: [action.payload, ...state.offboardings] };

        // ✅ OPTIMISTIC: update progression immédiatement
        case UPDATE_OFFBOARDING_REQUEST: {
            const { id, payload } = action.payload || {};
            if (!id || !payload) return state;

            return {
                ...state,
                offboardings: (state.offboardings || []).map((o) =>
                    String(o.id) === String(id) ? { ...o, ...payload } : o
                ),
                selectedOffboarding:
                    state.selectedOffboarding?.id && String(state.selectedOffboarding.id) === String(id)
                        ? { ...state.selectedOffboarding, ...payload }
                        : state.selectedOffboarding,
            };
        }

        // ✅ SUCCESS: merge (payload peut être partiel)
        case UPDATE_OFFBOARDING_SUCCESS: {
            const upd = normalizeEntityPayload(action.payload);
            if (!upd?.id) return state;

            return {
                ...state,
                offboardings: (state.offboardings || []).map((o) =>
                    String(o.id) === String(upd.id) ? { ...o, ...upd } : o
                ),
                selectedOffboarding:
                    state.selectedOffboarding?.id && String(state.selectedOffboarding.id) === String(upd.id)
                        ? { ...state.selectedOffboarding, ...upd }
                        : state.selectedOffboarding,
            };
        }

        case DELETE_OFFBOARDING_SUCCESS: {
            const id = action.payload;
            const etapesByOffboarding = { ...state.etapesByOffboarding };
            delete etapesByOffboarding[id];

            return {
                ...state,
                offboardings: (state.offboardings || []).filter((o) => String(o.id) !== String(id)),
                selectedOffboarding:
                    state.selectedOffboarding?.id && String(state.selectedOffboarding.id) === String(id)
                        ? null
                        : state.selectedOffboarding,
                etapesByOffboarding,
            };
        }

        // ===== ETAPES
        case FETCH_ETAPES_SUCCESS: {
            const { offboardingId, etapes } = action.payload || {};
            if (!offboardingId) return state;

            return {
                ...state,
                loading: false,
                error: null,
                etapesByOffboarding: {
                    ...state.etapesByOffboarding,
                    [String(offboardingId)]: etapes || [],
                },
            };
        }

        case FETCH_ETAPES_FAIL:
            return { ...state, loading: false, error: action.payload };

        case CREATE_ETAPE_SUCCESS: {
            const created = normalizeEntityPayload(action.payload);
            const offId = getOffIdFromEtape(created) ?? state.selectedOffboarding?.id;
            if (!offId) return state;

            const key = String(offId);
            const list = state.etapesByOffboarding[key] || [];

            return {
                ...state,
                etapesByOffboarding: {
                    ...state.etapesByOffboarding,
                    [key]: [created, ...list].sort((a, b) => (a.ordreEtape ?? 0) - (b.ordreEtape ?? 0)),
                },
            };
        }

        // ✅ OPTIMISTIC: au clic, on met COMPLETE immédiatement même si backend ne répond pas
        case UPDATE_ETAPE_REQUEST: {
            const { id, payload } = action.payload || {};
            if (!id || !payload) return state;

            const offId =
                payload.offboardingId ??
                payload.offboarding_id ??
                state.selectedOffboarding?.id ??
                findOffIdByEtapeId(state.etapesByOffboarding, id);

            if (!offId) return state;

            const key = String(offId);
            const list = state.etapesByOffboarding[key] || [];

            return {
                ...state,
                etapesByOffboarding: {
                    ...state.etapesByOffboarding,
                    [key]: list
                        .map((e) => (String(e.id) === String(id) ? { ...e, ...payload } : e))
                        .sort((a, b) => (a.ordreEtape ?? 0) - (b.ordreEtape ?? 0)),
                },
            };
        }

        case UPDATE_ETAPE_SUCCESS: {
            const updated = normalizeEntityPayload(action.payload);
            if (!updated?.id) return state;

            const offId =
                getOffIdFromEtape(updated) ??
                state.selectedOffboarding?.id ??
                findOffIdByEtapeId(state.etapesByOffboarding, updated.id);

            if (!offId) return state;

            const key = String(offId);
            const list = state.etapesByOffboarding[key] || [];

            return {
                ...state,
                etapesByOffboarding: {
                    ...state.etapesByOffboarding,
                    [key]: list
                        .map((e) => (String(e.id) === String(updated.id) ? { ...e, ...updated } : e))
                        .sort((a, b) => (a.ordreEtape ?? 0) - (b.ordreEtape ?? 0)),
                },
            };
        }

        case DELETE_ETAPE_SUCCESS: {
            const { id, offboardingId } = action.payload || {};
            if (!offboardingId) return state;

            const key = String(offboardingId);
            const list = state.etapesByOffboarding[key] || [];

            return {
                ...state,
                etapesByOffboarding: {
                    ...state.etapesByOffboarding,
                    [key]: list.filter((e) => String(e.id) !== String(id)),
                },
            };
        }

        default:
            return state;
    }
}
